import { useEffect, useRef, FC } from "react";
import { createLinkProps } from "../../utils/dataTransform/utils";
import classNames from "classnames";
import LinkWrap from "../LinkWrap/component";
import { useNormalizeClass } from "../../hooks/useNormalizeClass";
import AuthorInfo from "./components/AuthorInfo/component";
import CardDescription from "./components/Description/component";
import CardHeadline from "./components/Headline/component";
import CardMetadata from "./components/CardMetadata/component";
import CardThumbnail from "./components/CardThumbnail/component";
import CardFlag from "./components/CardFlag/component";
import CardPlaceholder from "./components/CardPlaceholder/component";
import { CardCQWData } from "./types";
import { CardVariantsEnum } from "../../utils/dataTransform/ContentQueryWidget/types";

const CardCQW: FC<CardCQWData> = ({
    flag,
    flagId,
    variant = CardVariantsEnum.STANDARD,
    sourceId,
    external,
    url,
    featureAttribution,
    clickHandler,
    id,
    focus,
    image,
    videoCardOverlayType,
    videoAirDate,
    videoDuration,
    videoHasCaptions,
    videoStreamType,
    headlineId,
    title,
    descriptionId,
    description,
    author,
    metadataId,
    publishTime,
    updateTime,
    departments,
    baseSection,
    subSection,
    displayComments,
}) => {
    const cardRef = useRef<HTMLAnchorElement>();

    useEffect(() => {
        if (focus && cardRef.current) {
            cardRef.current.focus();
        }
    }, [focus]);

    const flagClassName = useNormalizeClass("flag", flag);
    const classes = classNames("card", "card-cqw", variant, flagClassName);

    if (!id) {
        return <CardPlaceholder classes={classes} variant={variant} />;
    }

    const linkProps = createLinkProps({
        sourceId: sourceId,
        external: external,
        cardClickHandler: clickHandler,
        url: url,
    });

    return (
        <div
            data-feature-instance={featureAttribution?.instance}
            data-feature-name={featureAttribution?.name}
            data-feature-position={featureAttribution?.position}
        >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <LinkWrap
                id={id}
                ref={cardRef}
                className={classes}
                {...linkProps}
                data-testid="card-cqw"
                data-cy={id}
            >
                <div className="card-cqw-head">
                    <CardThumbnail
                        image={image}
                        variant={variant}
                        videoCardOverlayType={videoCardOverlayType}
                        videoAirDate={videoAirDate}
                        videoDuration={videoDuration}
                        videoHasCaptions={videoHasCaptions}
                        videoStreamType={videoStreamType}
                    />
                </div>
                <div className="card-cqw-body">
                    <div className="card-cqw-main">
                        <CardFlag
                            flag={flag}
                            flagId={flagId}
                            external={external}
                        />
                        <CardHeadline headlineId={headlineId} title={title} />
                        <CardDescription
                            descriptionId={descriptionId}
                            description={description}
                        />
                    </div>
                    <div className="card-cqw-bottom">
                        <AuthorInfo author={author} />
                        <CardMetadata
                            metadataId={metadataId}
                            publishTime={Number(publishTime)}
                            updateTime={Number(updateTime)}
                            departments={departments}
                            baseSection={baseSection}
                            subSection={subSection}
                            displayComments={displayComments}
                        />
                    </div>
                </div>
            </LinkWrap>
        </div>
    );
};

export default CardCQW;
